import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDataSource, type DataSourceId } from '../contexts/DataSourceContext';
import { useSourceReadModels } from '../hooks/useSourceReadModels';
import { recordSlice05ConnectionMarker } from '../lib/source-read-model/instrumentation';
import type { SourceReadModel } from '../lib/source-read-model/read-model';
import { startGoogleAuth, startMicrosoftAuth } from '../lib/oauth-integration';
import { startTauriOAuth } from '../utils/tauri-oauth';
import { getPlatform } from '../utils/platform';
import { loadPublicRuntimeConfig } from '../config/runtime-public-config';

function branchForSource(source: SourceReadModel): 'google' | 'microsoft' | 'plaid' | 'browser-extension' | 'location' | 'unsupported' {
  if (source.provider === 'google') return 'google';
  if (source.provider === 'microsoft') return 'microsoft';
  if (source.provider === 'plaid') return 'plaid';
  if (source.provider === 'browser-extension') return 'browser-extension';
  if (source.provider === 'location') return 'location';
  return 'unsupported';
}

function isDataSourceId(source: SourceReadModel): source is SourceReadModel & { id: DataSourceId } {
  return source.id !== 'location';
}

function UnsupportedReAuth({
  branch,
  sourceId,
  reason,
}: {
  branch: 'browser-extension' | 'location' | 'unsupported';
  sourceId: string;
  reason: string;
}) {
  return (
    <div
      data-testid="reauth-unsupported"
      data-source-id={sourceId}
      data-branch={branch}
      className="mx-auto max-w-xl rounded-lg border border-neutral-200 bg-white/85 p-6 text-center shadow-sm"
    >
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">ReAuth unavailable</p>
      <h1 className="mt-1 font-serif text-2xl font-semibold text-neutral-950">Use local source settings</h1>
      <p className="mt-2 text-sm text-neutral-600">{reason}</p>
      <Link className="mt-5 inline-flex rounded-md border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50" to="/connections">
        Back to Connections
      </Link>
    </div>
  );
}

export function ReAuth() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { connectSource } = useDataSource();
  const { isLoading, models, error } = useSourceReadModels();
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [plaidConfigState, setPlaidConfigState] = useState<'unchecked' | 'configured' | 'missing'>('unchecked');
  const source = models.find(model => model.id === id);
  const branch = source ? branchForSource(source) : 'unsupported';

  useEffect(() => {
    let cancelled = false;
    if (source?.provider !== 'plaid') {
      setPlaidConfigState('unchecked');
      return () => { cancelled = true; };
    }

    loadPublicRuntimeConfig()
      .then(config => {
        if (cancelled) return;
        setPlaidConfigState(config.plaidEdgeFunctionUrl ? 'configured' : 'missing');
      })
      .catch(() => {
        if (cancelled) return;
        setPlaidConfigState('missing');
      });

    return () => { cancelled = true; };
  }, [source?.provider]);

  useEffect(() => {
    if (isLoading || error) return;
    if (!source) {
      recordSlice05ConnectionMarker({
        kind: 'reauth_branch',
        status: 'blocked',
        branch: 'unsupported',
        reasonCode: 'unknown_source',
      });
      return;
    }
    if (source.id === 'location' || !source.supportsReauthRoute) {
      recordSlice05ConnectionMarker({
        kind: 'reauth_branch',
        status: 'blocked',
        sourceId: source.id,
        branch,
        reasonCode: 'unsupported_local_or_virtual_source',
      });
      return;
    }
    if (branch === 'plaid' && plaidConfigState === 'unchecked') return;
    recordSlice05ConnectionMarker({
      kind: 'reauth_branch',
      status: branch === 'plaid' && plaidConfigState === 'missing' ? 'blocked' : 'success',
      sourceId: source.id,
      branch,
      reasonCode: branch === 'plaid' && plaidConfigState === 'missing'
        ? 'missing_plaid_edge_config'
        : 'branch_rendered',
    });
  }, [branch, error, isLoading, plaidConfigState, source]);

  if (isLoading) {
    return (
      <div data-testid="reauth-loading" className="mx-auto max-w-xl rounded-lg border border-neutral-200 bg-white/70 p-5 text-sm text-neutral-500">
        Loading ReAuth...
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="reauth-error" className="mx-auto max-w-xl rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!source) {
    return (
      <UnsupportedReAuth
        branch="unsupported"
        sourceId={id ?? ''}
        reason="This source is not part of the local Connections read model."
      />
    );
  }

  const unsupportedReason = source.id === 'location'
    ? 'Location is a virtual permission source and does not use provider OAuth.'
    : !source.supportsReauthRoute
      ? 'This source uses local permission state rather than provider OAuth.'
      : null;

  if (unsupportedReason || !isDataSourceId(source)) {
    return (
      <UnsupportedReAuth
        branch={branch === 'browser-extension' || branch === 'location' ? branch : 'unsupported'}
        sourceId={source.id}
        reason={unsupportedReason ?? 'Unsupported ReAuth source.'}
      />
    );
  }

  if (branch === 'plaid' && plaidConfigState === 'missing') {
    return (
      <div
        data-testid="reauth-provider-problem"
        data-source-id={source.id}
        data-branch="plaid"
        className="mx-auto max-w-xl rounded-lg border border-red-200 bg-red-50 p-6 text-center shadow-sm"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-red-700">Provider problem</p>
        <h1 className="mt-1 font-serif text-2xl font-semibold text-neutral-950">{source.label}</h1>
        <p className="mt-2 text-sm text-red-700">
          Plaid edge function URL not configured.
        </p>
        <Link className="mt-5 inline-flex rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-red-50" to={`/connections/${source.id}`}>
          Back to detail
        </Link>
      </div>
    );
  }

  const runReconnect = async () => {
    setIsRunning(true);
    setRunError(null);
    const returnUrl = `/connections/${source.id}`;

    try {
      const platform = getPlatform();
      const effectiveBranch = platform === 'tauri' ? 'tauri' : branch;
      recordSlice05ConnectionMarker({
        kind: 'reauth_branch',
        status: 'success',
        sourceId: source.id,
        branch: effectiveBranch,
        reasonCode: 'branch_invoked',
      });

      if (platform === 'tauri') {
        const tokenData = await startTauriOAuth(source.id);
        if (tokenData) {
          await connectSource(source.id, tokenData);
          recordSlice05ConnectionMarker({
            kind: 'reauth_result',
            status: 'success',
            sourceId: source.id,
            branch: 'tauri',
            reasonCode: 'connected',
          });
          navigate(returnUrl);
        }
        return;
      }

      if (branch === 'google') {
        const tokens = await startGoogleAuth(returnUrl, source.id as 'gmail' | 'google-calendar');
        if (tokens) {
          await connectSource(source.id, {
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            expiresAt: tokens.expiresAt,
            provider: 'google',
          });
          recordSlice05ConnectionMarker({
            kind: 'reauth_result',
            status: 'success',
            sourceId: source.id,
            branch,
            reasonCode: 'connected',
          });
          navigate(returnUrl);
        }
        return;
      }

      if (branch === 'microsoft') {
        await startMicrosoftAuth(returnUrl);
        return;
      }

      if (branch === 'plaid') {
        recordSlice05ConnectionMarker({
          kind: 'reauth_result',
          status: 'skipped',
          sourceId: source.id,
          branch,
          reasonCode: 'plaid_link_live_update_deferred',
        });
        setRunError('Plaid Link reconnect is configured; live item-login-required evidence is deferred under no-sync scope.');
        return;
      }

      recordSlice05ConnectionMarker({
        kind: 'reauth_result',
        status: 'blocked',
        sourceId: source.id,
        branch,
        reasonCode: 'provider_branch_pending',
      });
      setRunError(`${source.label} reconnect is not available in this route yet.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Reconnect failed';
      setRunError(message);
      recordSlice05ConnectionMarker({
        kind: 'reauth_result',
        status: 'error',
        sourceId: source.id,
        branch,
        reasonCode: 'reconnect_failed',
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div
      data-testid="reauth-route"
      data-source-id={source.id}
      data-branch={branch}
      className="mx-auto flex min-h-[60vh] w-full max-w-xl items-center justify-center"
    >
      <section className="w-full rounded-lg border border-neutral-200 bg-white/90 p-6 text-center shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Reconnect source</p>
        <h1 className="mt-1 font-serif text-3xl font-semibold text-neutral-950">{source.label}</h1>
        <p className="mt-3 text-sm leading-6 text-neutral-600">
          Continue to the existing {source.provider} reconnect flow. OwnYou will return to SourceDetail after the provider finishes.
        </p>
        {source.authOutcome?.reason && (
          <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">{source.authOutcome.reason}</p>
        )}
        {runError && (
          <p data-testid="reauth-run-error" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{runError}</p>
        )}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            data-testid="reauth-start"
            className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={runReconnect}
            disabled={isRunning}
          >
            {isRunning ? 'Starting...' : 'Reconnect'}
          </button>
          <Link className="rounded-md border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50" to={`/connections/${source.id}`}>
            Back to detail
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ReAuth;
